/**
 * The jobcard state machine — the single source of truth.
 *
 * The button label, which actions are offered, whether the form is editable and
 * the server-side validation ALL read from this table. Nothing anywhere else
 * may hardcode a comparison against a status string; if you find yourself
 * writing `status === 'invoiced'` outside this file, add a helper here instead.
 */

export const JOBCARD_STATUSES = [
  'draft',
  'awaiting_approval',
  'in_progress',
  'completed',
  'invoiced',
  'paid',
  'cancelled',
] as const
export type JobcardStatus = (typeof JOBCARD_STATUSES)[number]

export const JOBCARD_ACTIONS = [
  'create_quotation',
  'start_work',
  'complete',
  'create_invoice',
  'approve',
  'reject',
  'mark_paid',
  'reopen',
  'cancel',
] as const
export type JobcardAction = (typeof JOBCARD_ACTIONS)[number]

export type DocumentType = 'quotation' | 'invoice'

/** How a button for this action should be rendered. */
export type ActionVariant = 'filled' | 'tonal' | 'outlined' | 'text' | 'danger'

export interface Transition {
  from: JobcardStatus
  action: JobcardAction
  to: JobcardStatus
  /** Button label shown to staff. */
  label: string
  /** Set when performing this action also issues a document. */
  issues?: DocumentType
  variant: ActionVariant
  /** Shown in a confirmation dialog when present. */
  confirm?: string
}

export const TRANSITIONS: readonly Transition[] = [
  { from: 'draft', action: 'create_quotation', to: 'awaiting_approval', label: 'Create quotation', issues: 'quotation', variant: 'filled' },
  { from: 'draft', action: 'start_work', to: 'in_progress', label: 'Start work', variant: 'tonal' },
  { from: 'draft', action: 'create_invoice', to: 'invoiced', label: 'Invoice directly', issues: 'invoice', variant: 'outlined', confirm: 'Invoice this jobcard without quoting it first? It becomes read-only.' },
  { from: 'draft', action: 'cancel', to: 'cancelled', label: 'Cancel', variant: 'danger', confirm: 'Cancel this jobcard?' },

  { from: 'awaiting_approval', action: 'approve', to: 'in_progress', label: 'Customer approved', variant: 'filled' },
  { from: 'awaiting_approval', action: 'reject', to: 'draft', label: 'Customer rejected', variant: 'outlined' },
  { from: 'awaiting_approval', action: 'create_quotation', to: 'awaiting_approval', label: 'Revise quotation', issues: 'quotation', variant: 'text' },
  { from: 'awaiting_approval', action: 'cancel', to: 'cancelled', label: 'Cancel', variant: 'danger', confirm: 'Cancel this jobcard?' },

  { from: 'in_progress', action: 'complete', to: 'completed', label: 'Mark as finished', variant: 'filled' },
  { from: 'in_progress', action: 'create_invoice', to: 'invoiced', label: 'Invoice directly', issues: 'invoice', variant: 'outlined', confirm: 'Invoice this jobcard without marking it finished first? It becomes read-only.' },
  { from: 'in_progress', action: 'cancel', to: 'cancelled', label: 'Cancel', variant: 'danger', confirm: 'Cancel this jobcard?' },

  { from: 'completed', action: 'create_invoice', to: 'invoiced', label: 'Create invoice', issues: 'invoice', variant: 'filled', confirm: 'Create the invoice? The jobcard becomes read-only.' },
  { from: 'completed', action: 'reopen', to: 'in_progress', label: 'Reopen (more work needed)', variant: 'outlined' },
  { from: 'completed', action: 'cancel', to: 'cancelled', label: 'Cancel', variant: 'danger', confirm: 'Cancel this jobcard?' },

  { from: 'invoiced', action: 'mark_paid', to: 'paid', label: 'Mark paid', variant: 'filled' },
  { from: 'invoiced', action: 'reopen', to: 'in_progress', label: 'Reopen', variant: 'outlined', confirm: 'Reopen this invoiced jobcard for editing? This is recorded on the timeline.' },

  { from: 'paid', action: 'reopen', to: 'in_progress', label: 'Reopen', variant: 'outlined', confirm: 'Reopen this PAID jobcard for editing? Only do this to correct a mistake — it is recorded on the timeline.' },
] as const

/** Statuses in which lines and details may be edited. */
const EDITABLE: readonly JobcardStatus[] = ['draft', 'awaiting_approval', 'in_progress', 'completed']
/**
 * Statuses counted as open on the dashboard.
 * Exported (not just `isOpen`/`isClosed`) so a query needs to filter by the
 * SET of open statuses — not just test one status at a time — can use it
 * directly instead of re-deriving its own copy of this list.
 */
export const OPEN_STATUSES: readonly JobcardStatus[] = ['draft', 'awaiting_approval', 'in_progress', 'completed']
/** Statuses counted as closed on the dashboard. `cancelled` is neither. */
export const CLOSED_STATUSES: readonly JobcardStatus[] = ['invoiced', 'paid']

export function isEditable(status: JobcardStatus): boolean {
  return EDITABLE.includes(status)
}
export function isOpen(status: JobcardStatus): boolean {
  return OPEN_STATUSES.includes(status)
}
export function isClosed(status: JobcardStatus): boolean {
  return CLOSED_STATUSES.includes(status)
}

/** Every action legal from this status, in the order they should be shown. */
export function availableActions(status: JobcardStatus): Transition[] {
  return TRANSITIONS.filter((t) => t.from === status)
}

/** The transition for this status/action pair, or null when it is illegal. */
export function resolveTransition(status: JobcardStatus, action: JobcardAction): Transition | null {
  return TRANSITIONS.find((t) => t.from === status && t.action === action) ?? null
}

/** The primary (most prominent) action for a status, if it has one. */
export function primaryAction(status: JobcardStatus): Transition | null {
  return availableActions(status).find((t) => t.variant === 'filled') ?? null
}

export const STATUS_LABELS: Record<JobcardStatus, string> = {
  draft: 'Draft',
  awaiting_approval: 'Waiting for approval',
  in_progress: 'In progress',
  completed: 'Completed',
  invoiced: 'Invoiced',
  paid: 'Paid',
  cancelled: 'Cancelled',
}

/** Column on the jobcards table stamped when a status is entered. */
export const STATUS_TIMESTAMP_COLUMN: Record<JobcardStatus, string | null> = {
  draft: null,
  awaiting_approval: 'quotedAt',
  in_progress: 'startedAt',
  completed: 'completedAt',
  invoiced: 'invoicedAt',
  paid: 'paidAt',
  cancelled: 'cancelledAt',
}

/**
 * Whether performing this transition requires a customer to be attached.
 *
 * Issuing a document is the line: a quotation has nobody to go to and a tax
 * invoice is not a valid tax document without a named recipient. Everything
 * before that — taking the car in, doing the work — is fine with an unknown
 * owner. The server enforces this and the UI reads the same helper, so the
 * button and the API can never disagree.
 */
export function transitionRequiresClient(transition: Transition): boolean {
  return Boolean(transition.issues)
}

/** Why an action is unavailable, or null when it can be performed. */
export function blockedReason(transition: Transition, hasClient: boolean): string | null {
  if (transitionRequiresClient(transition) && !hasClient) return 'Attach a customer first'
  return null
}
